// Turns the engine's /product-data/book-impact payload into display cards.
// Amounts are formatted, never invented: an unavailable metric stays
// unavailable with the engine's reason.

const eurFormatter = new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
    notation: 'compact',
    maximumFractionDigits: 1,
});

export const formatEur = (value) => (Number.isFinite(value) ? eurFormatter.format(value) : '—');

export const formatCount = (value) => Number(value || 0).toLocaleString('en-US');

const pct = (value, suffix) => (Number.isFinite(value) ? `${value.toFixed(1)}% ${suffix}` : null);

const CARD_DEFINITIONS = [
    {
        key: 'suitability_exposure',
        title: 'Holdings outside client profile',
        audience: 'Compliance',
        detail: (m) => [pct(m.pct_of_aum, 'of AUM'), `${formatCount(m.customers)} clients`].filter(Boolean).join(' · '),
    },
    {
        key: 'evidence_gap',
        title: 'Advised volume without evidence',
        audience: 'Compliance',
        detail: (m) => [pct(m.pct_of_advised_volume, 'of advised volume'), `${formatCount(m.transactions)} transactions`]
            .filter(Boolean).join(' · '),
    },
    {
        key: 'allocation_drift',
        title: 'Value outside model bands',
        audience: 'Portfolio',
        detail: (m) => [`${formatCount(m.customers)} clients`, Number.isFinite(m.worst_drift_pp) ? `worst ${m.worst_drift_pp} pp` : null]
            .filter(Boolean).join(' · '),
    },
    {
        key: 'behavioural_stress_exposure',
        title: 'AUM with elevated stress indicators',
        audience: 'Head of advisory',
        detail: (m) => [pct(m.pct_of_aum, 'of AUM'), `${formatCount(m.customers)} clients`].filter(Boolean).join(' · '),
    },
];

export const buildBookImpactCards = (payload) => {
    const metrics = payload?.metrics || {};
    return CARD_DEFINITIONS.map(({ key, title, audience, detail }) => {
        const metric = metrics[key];
        if (!metric || metric.status !== 'computed') {
            return {
                key, title, audience, available: false,
                value: 'Not available',
                detail: metric?.reason || 'Not computed for this workspace.',
                basis: metric?.basis || '',
            };
        }
        return {
            key, title, audience, available: true,
            value: formatEur(metric.value_eur),
            detail: detail(metric),
            basis: metric.basis,
        };
    });
};

export const buildAdviserTimeSummary = (payload) => {
    const metric = payload?.metrics?.adviser_time;
    if (!metric || metric.status !== 'computed') {
        return { available: false, text: metric?.reason || 'Measured only in a pilot.' };
    }
    const rates = metric.useful_rate_pct || {};
    const rate = (value) => (Number.isFinite(value) ? `${value}%` : 'n/a');
    return {
        available: true,
        text: `${metric.comparisons} blind comparison(s): useful cases ${rate(rates.FinanSee)} FinanSee vs `
            + `${rate(rates['Current method'])} current method; ${metric.baseline_preparation_minutes} min baseline `
            + `preparation vs ${metric.declared_review_minutes} min declared review.`,
    };
};
